import { createSignal, onCleanup, For, Show } from 'solid-js';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from './MessageBox';
import type { PlayerListAddEntryRequest, PlayerListSearchResultDto } from '../api';
import styles from './PlayerSearchPicker.module.css';

interface PlayerSearchPickerProps {
    onSelect: (player: PlayerListAddEntryRequest) => Promise<boolean>;
    placeholder?: string;
    disabled?: boolean;
}

const MINECRAFT_USERNAME = /^(?:[A-Za-z0-9_]{3,16}|![A-Za-z0-9_][A-Za-z0-9_+\-.]{1,14})$/;

// Search-as-you-type by name or UUID, backed by api.playerList.search. Modeled on
// ItemIdentifierPicker's live-dropdown pattern (onMouseDown-before-onBlur so a click on an option
// registers before the input's blur handler closes the dropdown).
export default function PlayerSearchPicker(props: PlayerSearchPickerProps) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [query, setQuery] = createSignal('');
    const [results, setResults] = createSignal<PlayerListSearchResultDto[]>([]);
    const [focused, setFocused] = createSignal(false);
    const [searching, setSearching] = createSignal(false);
    let debounceHandle: ReturnType<typeof setTimeout> | undefined;
    let latestRequestId = 0;

    const runSearch = async (q: string) => {
        if (q.trim().length === 0) { setResults([]); return; }
        const requestId = ++latestRequestId;
        setSearching(true);
        const res = await api.playerList.search({ q });
        if (requestId !== latestRequestId) return;
        if (isOk(res.data)) setResults(res.data.value.results ?? []);
        else toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
        setSearching(false);
    };

    const onInput = (value: string) => {
        setQuery(value);
        setResults([]);
        if (debounceHandle) clearTimeout(debounceHandle);
        debounceHandle = setTimeout(() => void runSearch(value), 250);
    };

    onCleanup(() => { if (debounceHandle) clearTimeout(debounceHandle); });

    const add = async (request: PlayerListAddEntryRequest) => {
        if (!await props.onSelect(request)) return;
        setQuery('');
        setResults([]);
    };

    // Names the local profile table has never seen are still addable: the add-entry endpoint
    // resolves them against Mojang and creates the profile.
    const addableName = () => {
        const trimmed = query().trim();
        if (!MINECRAFT_USERNAME.test(trimmed)) return null;
        if (results().some((p) => p.currentUsername?.toLowerCase() === trimmed.toLowerCase())) return null;
        return trimmed;
    };

    // Stays open while a search is in flight so a fresh query does not blink the add-option out
    // and back in; the input spinner carries the loading state instead.
    const dropdownVisible = () => focused() && query().trim().length > 0
        && (results().length > 0 || addableName() != null || !searching());

    return (
        <div class={styles.picker}>
            <div class={styles.searchWrap}>
                <i class={`fa-solid fa-magnifying-glass ${styles.searchIcon}`} />
                <input
                    type="text"
                    class={styles.searchInput}
                    value={query()}
                    onInput={(e) => onInput(e.currentTarget.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 120)}
                    placeholder={props.placeholder ?? t('PLAYERLIST_SEARCH_PLACEHOLDER')}
                    disabled={props.disabled}
                />
                <Show when={searching()}>
                    <i class={`fa-solid fa-spinner fa-spin ${styles.spinner}`} />
                </Show>
            </div>

            <Show when={dropdownVisible()}>
                <div class={styles.dropdown}>
                    <For each={results()}>
                        {(p) => (
                            <button type="button" class={styles.option}
                                onMouseDown={(e) => { e.preventDefault(); void add({ uuid: p.uuid }); }}>
                                <img src={`https://mc-heads.net/avatar/${p.uuid}/24`} alt="" class={styles.avatar} />
                                <span class={styles.optionName}>{p.currentUsername}</span>
                                <span class={styles.optionUuid}>{p.uuid}</span>
                            </button>
                        )}
                    </For>

                    <Show when={addableName()} keyed>
                        {(name) => (
                            <button type="button" class={styles.option}
                                onMouseDown={(e) => { e.preventDefault(); void add({ name }); }}>
                                <i class={`fa-solid fa-user-plus ${styles.addIcon}`} />
                                <span class={styles.optionName}>{t('PLAYERLIST_SEARCH_ADD_NAME', name)}</span>
                            </button>
                        )}
                    </Show>

                    <Show when={results().length === 0 && !addableName() && !searching()}>
                        <div class={styles.empty}><span>{t('PLAYERLIST_SEARCH_NO_RESULTS')}</span></div>
                    </Show>
                </div>
            </Show>
        </div>
    );
}
